// pages/ReportesDGII.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import { formatearFecha } from '../utils/dateUtils';

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];

const TIPOS_NCF = {
  // NCF tradicionales — código de 2 dígitos
  '01': 'Crédito Fiscal',
  '02': 'Consumidor Final',
  '03': 'Nota de Débito',
  '04': 'Nota de Crédito',
  '11': 'Comprobante de Compras',
  '12': 'Registro Único de Ingresos',
  '13': 'Gastos Menores',
  '14': 'Regímenes Especiales',
  '15': 'Gubernamental',
  '16': 'Exportaciones',
  '17': 'Pagos al Exterior',
  // Mismos tipos con el prefijo de serie — la serie B es NCF tradicional, no e-CF
  'B01': 'Crédito Fiscal',
  'B02': 'Consumidor Final',
  'B03': 'Nota de Débito',
  'B04': 'Nota de Crédito',
  'B11': 'Comprobante de Compras',
  'B12': 'Registro Único de Ingresos',
  'B13': 'Gastos Menores',
  'B14': 'Regímenes Especiales',
  'B15': 'Gubernamental',
  'B16': 'Exportaciones',
  'B17': 'Pagos al Exterior',
};

// Reportes cuyo backend valida el contrato DGII y bloquea la descarga cuando
// hay datos fiscales inválidos. Cada uno declara dónde se corrigen sus datos y
// cómo se llama la contraparte de la operación en su tabla de errores.
const REPORTES_CON_VALIDACION = {
  '606': { titulo: '606', ruta: '/cuentas-pagar', contraparte: 'Proveedor' },
  '607': { titulo: '607', ruta: '/facturas',      contraparte: 'Cliente'   },
};

const fmtMonto = (n) => new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2 }).format(n || 0);
const fmtFecha = (d) => formatearFecha(d) || '-';

export default function ReportesDGII() {
  const navigate = useNavigate();
  const hoy = new Date();

  const [tab,   setTab]   = useState('resumen');
  const [mes,   setMes]   = useState(hoy.getMonth() + 1);
  const [anio,  setAnio]  = useState(hoy.getFullYear());
  const [loading, setLoading]   = useState(false);
  const [resumen, setResumen]   = useState(null);
  const [datos606, setDatos606] = useState(null);
  const [datos607, setDatos607] = useState(null);
  const [datos608, setDatos608] = useState(null);
  const [datosIR17, setDatosIR17] = useState(null);
  const [datosIR2,  setDatosIR2]  = useState(null);
  const [datos609,  setDatos609]  = useState(null);
  const [datosIR3,  setDatosIR3]  = useState(null);
  const [erroresFiscales, setErroresFiscales] = useState(null); // { tipo, mensaje, detallados: [], generales: [] }
  const reporteRequestRef = useRef(0);

  const anios = Array.from({ length: 6 }, (_, i) => hoy.getFullYear() - i);

  // ─── Carga resumen ───
  const cargarResumen = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/resumen?mes=${mes}&anio=${anio}`);
      setResumen(data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo cargar el resumen', 'error');
    } finally {
      setLoading(false);
    }
  }, [mes, anio]);

  // ─── Carga reporte específico ───
  const cargarReporte = useCallback(async (tipo, mesSeleccionado = mes, anioSeleccionado = anio) => {
    const requestId = ++reporteRequestRef.current;
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/${tipo}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`);
      if (requestId !== reporteRequestRef.current) return;
      if (tipo === '606') setDatos606(data);
      if (tipo === '607') setDatos607(data);
      if (tipo === '608') setDatos608(data);
    } catch (e) {
      if (requestId === reporteRequestRef.current) {
        Swal.fire('Error', e.response?.data?.mensaje || `No se pudo cargar el reporte ${tipo}`, 'error');
      }
    } finally {
      if (requestId === reporteRequestRef.current) setLoading(false);
    }
  }, [mes, anio]);

  useEffect(() => {
    if (tab === '606') {
      setDatos606(null);
      cargarReporte('606', mes, anio);
    }
  }, [tab, mes, anio, cargarReporte]);

  useEffect(() => { cargarResumen(); }, [cargarResumen]);

  const cargarIR17 = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/ir17?mes=${mes}&anio=${anio}`);
      setDatosIR17(data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo cargar IR-17', 'error');
    } finally { setLoading(false); }
  }, [mes, anio]);

  const cargarIR2 = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/ir2?anio=${anio}`);
      setDatosIR2(data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo cargar IR-2', 'error');
    } finally { setLoading(false); }
  }, [anio]);

  const cargar609 = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/609?mes=${mes}&anio=${anio}`);
      setDatos609(data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo cargar el reporte 609', 'error');
    } finally { setLoading(false); }
  }, [mes, anio]);

  const cargarIR3 = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/ir3?anio=${anio}`);
      setDatosIR3(data);
    } catch (e) {
      Swal.fire('Error', e.response?.data?.mensaje || 'No se pudo cargar IR-3', 'error');
    } finally { setLoading(false); }
  }, [anio]);

  const handleTabChange = (t) => {
    setTab(t);
    if (t === '607'  && !datos607)  cargarReporte('607');
    if (t === '608'  && !datos608)  cargarReporte('608');
    if (t === 'ir17' && !datosIR17) cargarIR17();
    if (t === 'ir2'  && !datosIR2)  cargarIR2();
    if (t === '609'  && !datos609)  cargar609();
    if (t === 'ir3'  && !datosIR3)  cargarIR3();
  };

  const handlePeriodoChange = () => {
    setDatos606(null); setDatos607(null); setDatos608(null);
    setDatosIR17(null); setDatosIR2(null);
    setDatos609(null); setDatosIR3(null);
    cargarResumen();
    if (tab === '607')  cargarReporte('607');
    if (tab === '608')  cargarReporte('608');
    if (tab === 'ir17') cargarIR17();
    if (tab === 'ir2')  cargarIR2();
    if (tab === '609')  cargar609();
    if (tab === 'ir3')  cargarIR3();
  };

  // ─── Exportar TXT DGII ───
  const exportarTxt = async (tipo) => {
    try {
      const esSoloAnio = tipo === 'ir2' || tipo === 'ir3';
      const qs = esSoloAnio ? `anio=${anio}` : `mes=${mes}&anio=${anio}`;
      const sufijo = esSoloAnio ? `${anio}` : `${anio}${String(mes).padStart(2,'0')}`;
      const resp = await api.get(`/dgii/${tipo}/exportar?${qs}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'text/plain' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${tipo.toUpperCase()}_${sufijo}.txt`;
      link.click();
    } catch (e) {
      let mensaje = 'No se pudo exportar el archivo TXT';
      let payload = null;
      if (REPORTES_CON_VALIDACION[tipo] && e.response?.data instanceof Blob) {
        try {
          payload = JSON.parse(await e.response.data.text());
        } catch {
          // La respuesta no era JSON; conservar el mensaje genérico.
        }
      } else if (REPORTES_CON_VALIDACION[tipo] && e.response?.data?.mensaje) {
        payload = e.response.data;
      }
      if (payload?.mensaje) mensaje = payload.mensaje;

      // Si el backend envió errores estructurados, mostrar el modal con tabla; si no, alerta simple.
      const detallados = Array.isArray(payload?.erroresDetallados) ? payload.erroresDetallados : [];
      const generales  = Array.isArray(payload?.avisosGenerales)  ? payload.avisosGenerales  : [];
      if (REPORTES_CON_VALIDACION[tipo] && (detallados.length || generales.length)) {
        setErroresFiscales({ tipo, mensaje, detallados, generales });
      } else {
        Swal.fire('Error', mensaje, 'error');
      }
    }
  };

  // ─── Exportar Excel (genérico) ───
  // El backend es la autoridad: si el reporte no valida, responde 4xx con los
  // hallazgos y aquí NO se crea ni se descarga ningún archivo.
  const exportarExcel = async (tipo) => {
    try {
      const esSoloAnio = tipo === 'ir2' || tipo === 'ir3';
      const qs = esSoloAnio ? `anio=${anio}` : `mes=${mes}&anio=${anio}`;
      const sufijo = esSoloAnio ? `${anio}` : `${anio}${String(mes).padStart(2,'0')}`;
      const resp = await api.get(`/dgii/${tipo}/exportar-excel?${qs}`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${tipo.toUpperCase()}_${sufijo}.xlsx`;
      link.click();
    } catch (e) {
      let mensaje = 'No se pudo exportar el archivo Excel';
      let payload = null;
      if (e.response?.data instanceof Blob) {
        try {
          payload = JSON.parse(await e.response.data.text());
        } catch {
          // La respuesta no era JSON; conservar el mensaje genérico.
        }
      } else if (e.response?.data?.mensaje) {
        payload = e.response.data;
      }
      if (payload?.mensaje) mensaje = payload.mensaje;

      // Mismos hallazgos fiscales que muestra el TXT, en el mismo modal.
      const detallados = Array.isArray(payload?.erroresDetallados) ? payload.erroresDetallados : [];
      const generales = Array.isArray(payload?.avisosGenerales) ? payload.avisosGenerales : [];
      if (REPORTES_CON_VALIDACION[tipo] && (detallados.length || generales.length)) {
        setErroresFiscales({ tipo, mensaje, detallados, generales });
      } else {
        Swal.fire('Error', mensaje, 'error');
      }
    }
  };

  // ─── Render ───
  const periodoLabel = `${MESES[mes - 1]} ${anio}`;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <button onClick={() => navigate('/facturas')} style={s.btnVolver}>
            ← Volver
          </button>
          <h1 style={s.titulo}>Reportes DGII</h1>
          <p style={s.subtitulo}>Formatos 606 · 607 · 608 — {periodoLabel}</p>
        </div>
        <div style={s.filtrosRow}>
          <select value={mes} onChange={e => setMes(Number(e.target.value))} style={s.select}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={s.select}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button onClick={handlePeriodoChange} style={s.btnPrimario} disabled={loading}>
            {loading ? 'Cargando...' : '🔄 Actualizar'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabsBar}>
        {[
          { key: 'resumen', label: '📊 Resumen' },
          { key: '607',     label: '📤 607 – Ventas' },
          { key: '606',     label: '📥 606 – Compras' },
          { key: '608',     label: '🚫 608 – Anulados' },
          { key: '609',     label: '🌐 609 – Pagos Exterior' },
          { key: 'it1',     label: '🧾 IT-1 ITBIS' },
          { key: 'ir17',    label: '👷 IR-17 Retenciones' },
          { key: 'ir2',     label: '🏢 IR-2 ISR Anual' },
          { key: 'ir3',     label: '👤 IR-3 Neg. Único Dueño' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => handleTabChange(t.key)}
            style={{ ...s.tabBtn, ...(tab === t.key ? s.tabActivo : {}) }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div style={s.contenido}>
        {tab === 'resumen' && <TabResumen resumen={resumen} loading={loading} mes={mes} anio={anio} periodoLabel={periodoLabel} exportarExcel={() => exportarExcel('resumen')} />}
        {tab === '607' && <Tab607 datos={datos607} loading={loading} exportar={() => exportarTxt('607')} exportarExcel={() => exportarExcel('607')} cargar={() => cargarReporte('607')} />}
        {tab === '606' && <Tab606 datos={datos606} loading={loading} exportar={() => exportarTxt('606')} exportarExcel={() => exportarExcel('606')} cargar={() => cargarReporte('606')} />}
        {tab === '608' && <Tab608 datos={datos608} loading={loading} exportar={() => exportarTxt('608')} exportarExcel={() => exportarExcel('608')} cargar={() => cargarReporte('608')} />}
        {tab === 'it1'  && <TabIT1  mes={mes} anio={anio} />}
        {tab === 'ir17' && <TabIR17 datos={datosIR17} loading={loading} mes={mes} anio={anio} exportar={() => exportarTxt('ir17')} exportarExcel={() => exportarExcel('ir17')} cargar={cargarIR17} />}
        {tab === 'ir2'  && <TabIR2  datos={datosIR2}  loading={loading} anio={anio} exportar={() => exportarTxt('ir2')}  cargar={cargarIR2}  />}
        {tab === '609'  && <Tab609  datos={datos609}  loading={loading} mes={mes} anio={anio}
                                    exportarTxt={() => exportarTxt('609')}
                                    exportarExcel={() => exportarExcel('609')}
                                    recargar={cargar609} />}
        {tab === 'ir3'  && <TabIR3  datos={datosIR3}  loading={loading} anio={anio} exportar={() => exportarTxt('ir3')}  cargar={cargarIR3}  />}
      </div>

      {erroresFiscales && (
        <ModalErroresFiscales
          data={erroresFiscales}
          onCerrar={() => setErroresFiscales(null)}
          onIrACorregir={() => {
            const destino = REPORTES_CON_VALIDACION[erroresFiscales.tipo]?.ruta || '/cuentas-pagar';
            setErroresFiscales(null);
            navigate(destino);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab Resumen
// ─────────────────────────────────────────────
function TabResumen({ resumen, loading, periodoLabel, exportarExcel }) {
  if (loading) return <Spinner />;
  if (!resumen) return null;

  const { ventas_607, compras_606, anulados_608 } = resumen;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={exportarExcel} style={{ ...s.btnExportar, background: '#217346' }}>
          ⬇ Exportar Excel
        </button>
      </div>
      <div style={s.kpiGrid}>
        <KpiCard
          icono="📤"
          titulo="Reporte 607 – Ventas"
          valor={`RD$ ${fmtMonto(ventas_607?.total)}`}
          detalle={`${ventas_607?.registros} comprobantes · ITBIS: RD$ ${fmtMonto(ventas_607?.itbis)}`}
          color="#0066cc"
          bg="#eff6ff"
        />
        <KpiCard
          icono="📥"
          titulo="Reporte 606 – Compras"
          valor={`RD$ ${fmtMonto(compras_606?.total)}`}
          detalle={`${compras_606?.registros} comprobantes · ITBIS: RD$ ${fmtMonto(compras_606?.itbis)}`}
          color="#059669"
          bg="#f0fdf4"
        />
        <KpiCard
          icono="🚫"
          titulo="Reporte 608 – Anulados"
          valor={`${anulados_608?.registros} comprobantes`}
          detalle="Comprobantes anulados en el período"
          color="#dc2626"
          bg="#fef2f2"
        />
        <KpiCard
          icono="🧾"
          titulo="ITBIS Neto del Período"
          valor={`RD$ ${fmtMonto((ventas_607?.itbis || 0) - (compras_606?.itbis || 0))}`}
          detalle={`Ventas: ${fmtMonto(ventas_607?.itbis)} · Compras: ${fmtMonto(compras_606?.itbis)}`}
          color="#7c3aed"
          bg="#f5f3ff"
        />
      </div>

      <div style={s.card}>
        <h3 style={s.cardTitulo}>📋 Guía de declaración — {periodoLabel}</h3>
        <div style={s.tablaSimple}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Formato','Descripción','Registros','Total RD$','Estado'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={s.tr}>
                <td style={s.td}><span style={{ ...s.badge, background: '#dbeafe', color: '#1e40af' }}>606</span></td>
                <td style={s.td}>Relación de Compras</td>
                <td style={s.td}>{compras_606?.registros}</td>
                <td style={s.td}>{fmtMonto(compras_606?.total)}</td>
                <td style={s.td}><span style={{ ...s.badge, background: '#d1fae5', color: '#065f46' }}>✓ Listo</span></td>
              </tr>
              <tr style={s.tr}>
                <td style={s.td}><span style={{ ...s.badge, background: '#dbeafe', color: '#1e40af' }}>607</span></td>
                <td style={s.td}>Relación de Ventas</td>
                <td style={s.td}>{ventas_607?.registros}</td>
                <td style={s.td}>{fmtMonto(ventas_607?.total)}</td>
                <td style={s.td}><span style={{ ...s.badge, background: '#d1fae5', color: '#065f46' }}>✓ Listo</span></td>
              </tr>
              <tr style={s.tr}>
                <td style={s.td}><span style={{ ...s.badge, background: '#dbeafe', color: '#1e40af' }}>608</span></td>
                <td style={s.td}>Comprobantes Anulados</td>
                <td style={s.td}>{anulados_608?.registros}</td>
                <td style={s.td}>—</td>
                <td style={s.td}><span style={{ ...s.badge, background: '#d1fae5', color: '#065f46' }}>✓ Listo</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p style={{ color: '#6c757d', fontSize: '0.85rem', marginTop: '1rem' }}>
          💡 Usa las pestañas para ver el detalle de cada reporte y exportar el archivo TXT para subir a la OFV de la DGII.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab 607 – Ventas
// ─────────────────────────────────────────────
function Tab607({ datos, loading, exportar, exportarExcel, cargar }) {
  if (loading) return <Spinner />;
  if (!datos)  return <BtnCargar onCargar={cargar} label="Cargar reporte 607" />;

  return (
    <div>
      <div style={s.reporteHeader}>
        <div>
          <h3 style={s.cardTitulo}>📤 Formato 607 – Relación de Ventas</h3>
          <p style={s.subtituloSeccion}>
            {datos.total_registros} comprobantes · Total RD$ {fmtMonto(datos.totales?.total)} · ITBIS RD$ {fmtMonto(datos.totales?.itbis_facturado)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={exportar} style={s.btnExportar}>
            ⬇ Exportar TXT (DGII)
          </button>
          <button onClick={exportarExcel} style={{ ...s.btnExportar, background: '#217346' }}>
            ⬇ Exportar Excel
          </button>
        </div>
      </div>

      <div style={s.kpiGrid3}>
        <KpiMini titulo="Monto Facturado" valor={`RD$ ${fmtMonto(datos.totales?.monto_facturado)}`} color="#0066cc" />
        <KpiMini titulo="ITBIS Total"     valor={`RD$ ${fmtMonto(datos.totales?.itbis_facturado)}`} color="#059669" />
        <KpiMini titulo="Total Cobrado"   valor={`RD$ ${fmtMonto(datos.totales?.total)}`}           color="#7c3aed" />
      </div>

      {datos.registros.length === 0 ? (
        <VacioMsg mensaje="No hay comprobantes emitidos en este período" />
      ) : (
        <div style={s.tablaWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['RNC/Cédula','Tipo ID','Tipo NCF','NCF','Fecha','Cliente','Monto','ITBIS','Total','Estado'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.registros.map((r, i) => (
                <tr key={i} style={{ ...s.tr, background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={s.td}>{r.rnc_cedula}</td>
                  <td style={s.td}><span style={s.tipoChip}>{r.tipo_id === '1' ? 'RNC' : r.tipo_id === '2' ? 'CÉD' : 'CF'}</span></td>
                  <td style={s.td}><span style={s.ncfChip}>{r.tipo_ncf} – {TIPOS_NCF[r.tipo_ncf] || ''}</span></td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 600 }}>{r.ncf}</td>
                  <td style={s.td}>{fmtFecha(r.fecha_comprobante)}</td>
                  <td style={s.td}>{r.cliente}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtMonto(r.monto_facturado)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtMonto(r.itbis_facturado)}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmtMonto(r.total)}</td>
                  <td style={s.td}><EstadoBadge estado={r.estado} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab 606 – Compras
// ─────────────────────────────────────────────
function Tab606({ datos, loading, exportar, exportarExcel, cargar }) {
  if (loading) return <Spinner />;
  if (!datos)  return <BtnCargar onCargar={cargar} label="Cargar reporte 606" />;

  return (
    <div>
      <div style={s.reporteHeader}>
        <div>
          <h3 style={s.cardTitulo}>📥 Formato 606 – Relación de Compras</h3>
          <p style={s.subtituloSeccion}>
            {datos.total_registros} comprobantes · Total RD$ {fmtMonto(datos.totales?.total_facturado)} · ITBIS RD$ {fmtMonto(datos.totales?.itbis)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={exportar} style={s.btnExportar}>
            ⬇ Exportar TXT (DGII)
          </button>
          <button onClick={exportarExcel} style={{ ...s.btnExportar, background: '#217346' }}>
            ⬇ Exportar Excel
          </button>
        </div>
      </div>

      <div style={s.kpiGrid3}>
        <KpiMini titulo="Monto Bienes"   valor={`RD$ ${fmtMonto(datos.totales?.monto_bienes)}`}    color="#0066cc" />
        <KpiMini titulo="ITBIS Total"    valor={`RD$ ${fmtMonto(datos.totales?.itbis)}`}            color="#059669" />
        <KpiMini titulo="Total Compras"  valor={`RD$ ${fmtMonto(datos.totales?.total_facturado)}`}  color="#7c3aed" />
      </div>

      <div style={{ ...s.card, background: '#fffbeb', border: '1px solid #fcd34d', marginBottom: '1rem' }}>
        <p style={{ margin: 0, color: '#92400e', fontSize: '0.875rem' }}>
          💡 <strong>Tip DGII:</strong> Para completar el 606, registra el NCF del proveedor en cada Orden de Compra al momento de recibirla. Los campos <em>NCF Proveedor</em> y <em>Tipo NCF</em> ya están disponibles en el formulario de órdenes de compra.
        </p>
      </div>

      {datos.registros.length === 0 ? (
        <VacioMsg mensaje="No hay compras registradas en este período" />
      ) : (
        <div style={s.tablaWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['RNC Proveedor','Tipo NCF','NCF Proveedor','Fecha','Proveedor','Monto','ITBIS','Total','OC #'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.registros.map((r, i) => (
                <tr key={i} style={{ ...s.tr, background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={s.td}>{r.rnc_cedula}</td>
                  <td style={s.td}><span style={s.ncfChip}>{r.tipo_ncf}</span></td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 600 }}>{r.ncf || <span style={{ color: '#f59e0b' }}>⚠ Pendiente</span>}</td>
                  <td style={s.td}>{fmtFecha(r.fecha_comprobante)}</td>
                  <td style={s.td}>{r.proveedor}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtMonto(r.monto_bienes)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtMonto(r.itbis)}</td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmtMonto(r.total_facturado)}</td>
                  <td style={s.td}>{r.numero_orden}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab 608 – Anulados
// ─────────────────────────────────────────────
function Tab608({ datos, loading, exportar, exportarExcel, cargar }) {
  if (loading) return <Spinner />;
  if (!datos)  return <BtnCargar onCargar={cargar} label="Cargar reporte 608" />;

  return (
    <div>
      <div style={s.reporteHeader}>
        <div>
          <h3 style={s.cardTitulo}>🚫 Formato 608 – Comprobantes Anulados</h3>
          <p style={s.subtituloSeccion}>{datos.total_registros} comprobantes anulados en el período</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={exportar} style={s.btnExportar}>
            ⬇ Exportar TXT (DGII)
          </button>
          <button onClick={exportarExcel} style={{ ...s.btnExportar, background: '#217346' }}>
            ⬇ Exportar Excel
          </button>
        </div>
      </div>

      {datos.registros.length === 0 ? (
        <VacioMsg mensaje="No hay comprobantes anulados en este período" icono="✅" color="#065f46" bg="#d1fae5" />
      ) : (
        <div style={s.tablaWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Tipo NCF','NCF','Factura #','Fecha Comprobante','Fecha Anulación'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {datos.registros.map((r, i) => (
                <tr key={i} style={{ ...s.tr, background: i % 2 === 0 ? '#fff' : '#fff5f5' }}>
                  <td style={s.td}><span style={s.ncfChip}>{r.tipo_ncf}</span></td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 600 }}>{r.ncf}</td>
                  <td style={s.td}>{r.numero_factura}</td>
                  <td style={s.td}>{fmtFecha(r.fecha_comprobante)}</td>
                  <td style={{ ...s.td, color: '#dc2626' }}>{fmtFecha(r.fecha_anulacion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Componentes auxiliares
// ─────────────────────────────────────────────
function KpiCard({ icono, titulo, valor, detalle, color, bg }) {
  return (
    <div style={{ background: bg, borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '1.25rem 1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
      <div style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>{icono}</div>
      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>{titulo}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e' }}>{valor}</div>
      <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.25rem' }}>{detalle}</div>
    </div>
  );
}

function KpiMini({ titulo, valor, color }) {
  return (
    <div style={{ background: '#fff', border: `1px solid #e2e8f0`, borderTop: `3px solid ${color}`, borderRadius: 8, padding: '1rem 1.25rem', textAlign: 'center' }}>
      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{titulo}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1a1a2e', marginTop: '0.25rem' }}>{valor}</div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const map = {
    emitida: { bg: '#dbeafe', color: '#1e40af' },
    pagada:  { bg: '#d1fae5', color: '#065f46' },
    parcial: { bg: '#fef3c7', color: '#92400e' },
    vencida: { bg: '#fed7aa', color: '#9a3412' },
  };
  const st = map[estado] || { bg: '#f3f4f6', color: '#4b5563' };
  return <span style={{ ...s.badge, background: st.bg, color: st.color }}>{estado}</span>;
}

function Spinner() {
  return (
    <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
      Cargando datos...
    </div>
  );
}

function VacioMsg({ mensaje, icono = '📭', color = '#6c757d', bg = '#f8f9fa' }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem', background: bg, borderRadius: 10 }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>{icono}</div>
      <p style={{ color, fontWeight: 600, margin: 0 }}>{mensaje}</p>
    </div>
  );
}

function BtnCargar({ onCargar, label }) {
  return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <p style={{ color: '#6c757d', marginBottom: '1rem' }}>Haz clic para cargar los datos del período seleccionado.</p>
      <button onClick={onCargar} style={s.btnPrimario}>{label}</button>
    </div>
  );
}

// ─────────────────────────────────────────────
//  IT-1 — Declaración mensual ITBIS
// ─────────────────────────────────────────────
function TabIT1({ mes, anio }) {
  const [datos,   setDatos]   = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dgii/it1?mes=${mes}&anio=${anio}`);
      setDatos(data);
    } catch (e) {
      Swal.fire('Error', 'No se pudo calcular el IT-1', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, [mes, anio]);

  const fmt = (n) => new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2 }).format(n || 0);

  const colorResultado = datos?.liquidacion?.resultado === 'A PAGAR'
    ? '#dc2626' : datos?.liquidacion?.resultado === 'A FAVOR' ? '#16a34a' : '#6b7280';

  return (
    <div style={{ padding: '1.5rem 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1a1a2e' }}>IT-1 — Declaración Mensual ITBIS</h2>
          <p style={{ margin: '0.25rem 0 0 0', color: '#64748b', fontSize: '0.9rem' }}>
            Período: {datos ? `${MESES[datos.mes - 1]} ${datos.anio}` : '...'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={cargar} style={{ padding: '0.5rem 1.25rem', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }} disabled={loading}>
            {loading ? 'Calculando...' : 'Recalcular'}
          </button>
          <button onClick={() => window.open(`/api/dgii/it1/exportar?mes=${mes}&anio=${anio}`, '_blank')}
            style={{ padding: '0.5rem 1.25rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            Exportar TXT
          </button>
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Calculando...</div>}

      {datos && !loading && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Débito Fiscal */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#0369a1', borderBottom: '2px solid #0369a1', paddingBottom: '0.5rem' }}>
              Débito Fiscal (ITBIS Cobrado)
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.5rem 0', color: '#64748b' }}>Base ventas - Facturación</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>RD$ {fmt(datos.debito_fiscal.base_ventas_facturacion)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.5rem 0', color: '#64748b' }}>ITBIS ventas - Facturación</td>
                  <td style={{ textAlign: 'right', fontWeight: 500, color: '#0369a1' }}>RD$ {fmt(datos.debito_fiscal.itbis_ventas_facturacion)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.5rem 0', color: '#64748b' }}>Base ventas - POS</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>RD$ {fmt(datos.debito_fiscal.base_ventas_pos)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.5rem 0', color: '#64748b' }}>ITBIS ventas - POS</td>
                  <td style={{ textAlign: 'right', fontWeight: 500, color: '#0369a1' }}>RD$ {fmt(datos.debito_fiscal.itbis_ventas_pos)}</td>
                </tr>
                <tr style={{ background: '#eff6ff' }}>
                  <td style={{ padding: '0.75rem 0.5rem', fontWeight: 700 }}>Total Débito Fiscal</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1', fontSize: '1.05rem' }}>RD$ {fmt(datos.debito_fiscal.total_debito)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Crédito Fiscal */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem 0', color: '#7c3aed', borderBottom: '2px solid #7c3aed', paddingBottom: '0.5rem' }}>
              Crédito Fiscal (ITBIS Pagado)
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.5rem 0', color: '#64748b' }}>Base compras proveedores</td>
                  <td style={{ textAlign: 'right', fontWeight: 500 }}>RD$ {fmt(datos.credito_fiscal.base_compras)}</td>
                </tr>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.5rem 0', color: '#64748b' }}>ITBIS en compras</td>
                  <td style={{ textAlign: 'right', fontWeight: 500, color: '#7c3aed' }}>RD$ {fmt(datos.credito_fiscal.itbis_compras)}</td>
                </tr>
                <tr style={{ background: '#f5f3ff' }}>
                  <td style={{ padding: '0.75rem 0.5rem', fontWeight: 700 }}>Total Crédito Fiscal</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontSize: '1.05rem' }}>RD$ {fmt(datos.credito_fiscal.total_credito)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Liquidación */}
          <div style={{ gridColumn: '1 / -1', background: '#fff', border: `2px solid ${colorResultado}`, borderRadius: 12, padding: '1.5rem', textAlign: 'center' }}>
            <p style={{ margin: '0 0 0.5rem 0', color: '#64748b', fontSize: '0.9rem' }}>Resultado del período</p>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: colorResultado }}>
              {datos.liquidacion.resultado}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: colorResultado, marginTop: '0.25rem' }}>
              RD$ {fmt(datos.liquidacion.monto)}
            </div>
            <p style={{ margin: '0.75rem 0 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
              {datos.liquidacion.resultado === 'A PAGAR'
                ? 'Este monto debe pagarse a la DGII antes del día 20 del próximo mes.'
                : datos.liquidacion.resultado === 'A FAVOR'
                ? 'El crédito fiscal supera el débito. Puede aplicarse al próximo período o solicitar reembolso.'
                : 'El ITBIS cobrado y pagado están en equilibrio.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab IR-17 — Retenciones Mensuales
// ─────────────────────────────────────────────
function TabIR17({ datos, loading, mes, anio, exportar, exportarExcel, cargar }) {
  const MESES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const fmt = (n) => fmtMonto(parseFloat(n) || 0);

  if (loading) return <Spinner />;
  if (!datos) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <p style={{ color: '#64748b', marginBottom: '1rem' }}>Carga el reporte IR-17 para {MESES_ES[mes-1]} {anio}</p>
      <button onClick={cargar} style={{ padding: '10px 24px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
        Generar IR-17
      </button>
    </div>
  );

  const { asalariados, proveedores, totales } = datos;
  const cardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' };
  const thStyle   = { textAlign: 'left', padding: '8px 12px', background: '#f8f9fa', borderBottom: '2px solid #dee2e6', fontSize: 13 };
  const tdStyle   = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 13 };

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'ISR Asalariados',    value: `RD$ ${fmt(asalariados.total_isr_retenido)}`,  color: '#dc3545' },
          { label: 'TSS Empleado',       value: `RD$ ${fmt(asalariados.total_tss_empleado)}`,  color: '#fd7e14' },
          { label: 'TSS Patronal',       value: `RD$ ${fmt(asalariados.total_tss_patronal)}`,  color: '#6f42c1' },
          { label: 'Total a Declarar',   value: `RD$ ${fmt(totales.total_a_declarar)}`,         color: '#0d6efd' },
        ].map(k => (
          <div key={k.label} style={{ background: '#fff', border: `1px solid ${k.color}33`, borderLeft: `4px solid ${k.color}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#888' }}>{k.label}</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={exportar} style={{ padding: '8px 20px', background: '#198754', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          ⬇ Exportar TXT
        </button>
        <button onClick={exportarExcel} style={{ padding: '8px 20px', marginLeft: 8, background: '#217346', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          ⬇ Exportar Excel
        </button>
      </div>

      {/* Sección A — Asalariados */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, borderBottom: '2px solid #dc3545', paddingBottom: 8, color: '#dc3545' }}>
          Sección A — Retenciones a Asalariados ({asalariados.registros} registros)
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <colgroup>
              <col style={{ width: '9%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
            </colgroup>
            <thead>
              <tr>
                {[
                  { label: 'Período',  align: 'left'  },
                  { label: 'Empleado', align: 'left'  },
                  { label: 'Cédula',   align: 'left'  },
                  { label: 'Bruto',    align: 'right' },
                  { label: 'TSS Emp',  align: 'right' },
                  { label: 'TSS Pat',  align: 'right' },
                  { label: 'ISR',      align: 'right' },
                  { label: 'Neto',     align: 'right' },
                ].map(h => (
                  <th key={h.label} style={{ ...thStyle, textAlign: h.align, paddingRight: h.align === 'right' ? 12 : 8 }}>{h.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {asalariados.detalle.length === 0 && (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#aaa' }}>Sin registros en este período</td></tr>
              )}
              {asalariados.detalle.map((a, i) => (
                <tr key={i}>
                  <td style={tdStyle}><code style={{ fontSize: 11 }}>{a.periodo}</code></td>
                  <td style={tdStyle}><strong>{a.empleado}</strong></td>
                  <td style={tdStyle}>{a.cedula || '-'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12 }}>{fmt(a.salario_bruto)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#fd7e14' }}>{fmt(a.tss_empleado)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#6f42c1' }}>{fmt(a.tss_patronal)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#dc3545', fontWeight: 700 }}>{fmt(a.isr_retenido)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#28a745' }}>{fmt(a.salario_neto)}</td>
                </tr>
              ))}
              <tr style={{ background: '#f8f9fa', fontWeight: 700 }}>
                <td colSpan={3} style={tdStyle}>TOTALES</td>
                <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12 }}>{fmt(asalariados.total_salario_bruto)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#fd7e14' }}>{fmt(asalariados.total_tss_empleado)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#6f42c1' }}>{fmt(asalariados.total_tss_patronal)}</td>
                <td style={{ ...tdStyle, textAlign: 'right', paddingRight: 12, color: '#dc3545' }}>{fmt(asalariados.total_isr_retenido)}</td>
                <td style={tdStyle}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Sección B — Proveedores */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, borderBottom: '2px solid #0d6efd', paddingBottom: 8, color: '#0d6efd' }}>
          Sección B — Retenciones a Proveedores/Honorarios ({proveedores.registros} registros)
        </h3>
        {proveedores.registros === 0 ? (
          <p style={{ color: '#aaa', textAlign: 'center' }}>No hay retenciones a proveedores en este período.<br/>
            <small>Las retenciones se registran desde Cuentas por Pagar cuando el proveedor es persona física.</small>
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>{['Proveedor','RNC','Bruto','ISR','ITBIS','Retención','Neto','Fecha'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {proveedores.detalle.map((p, i) => (
                <tr key={i}>
                  <td style={tdStyle}>{p.proveedor}</td>
                  <td style={tdStyle}>{p.rnc}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>RD$ {fmt(p.monto_facturado)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#dc3545' }}>RD$ {fmt(p.retencion_isr)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#fd7e14' }}>RD$ {fmt(p.retencion_itbis)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#dc3545', fontWeight: 700 }}>RD$ {fmt(p.retencion)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>RD$ {fmt(p.monto_neto_pagar)}</td>
                  <td style={tdStyle}>{fmtFecha(p.fecha)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab IR-2 — ISR Anual Personas Jurídicas
// ─────────────────────────────────────────────
function TabIR2({ datos, loading, anio, exportar, cargar }) {
  const fmt = (n) => fmtMonto(parseFloat(n) || 0);
  const cardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' };

  if (loading) return <Spinner />;
  if (!datos) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <p style={{ color: '#64748b', marginBottom: '1rem' }}>Carga la declaración IR-2 para el año fiscal {anio}</p>
      <button onClick={cargar} style={{ padding: '10px 24px', background: '#0d6efd', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
        Generar IR-2
      </button>
    </div>
  );

  const { estado_resultados: er, liquidacion: liq } = datos;
  const colorResultado = liq.resultado === 'UTILIDAD' ? '#28a745' : '#dc3545';

  const SeccionCuentas = ({ titulo, filas, total, color }) => (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 8px', color, borderBottom: `1px solid ${color}44`, paddingBottom: 4 }}>{titulo}</h4>
      {filas.length === 0
        ? <p style={{ color: '#aaa', fontSize: 13 }}>Sin movimientos</p>
        : filas.map((f, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14, borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ color: '#555' }}><code style={{ fontSize: 11, color: '#888' }}>{f.codigo}</code> {f.nombre}</span>
            <span style={{ fontWeight: 600 }}>RD$ {fmt(f.monto)}</span>
          </div>
        ))
      }
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 8, padding: '6px 0', borderTop: `2px solid ${color}` }}>
        <span>Total</span>
        <span style={{ color }}>RD$ {fmt(total)}</span>
      </div>
    </div>
  );

  return (
    <div>
      {/* KPIs liquidación */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Ingresos',        value: `RD$ ${fmt(er.total_ingresos)}`, color: '#28a745' },
          { label: 'Costos + Gastos', value: `RD$ ${fmt(er.total_costos + er.total_gastos)}`, color: '#dc3545' },
          { label: 'Renta Neta',      value: `RD$ ${fmt(liq.renta_neta_imponible)}`, color: colorResultado },
          { label: `ISR 27%`,         value: `RD$ ${fmt(liq.isr_causado)}`, color: '#6f42c1' },
          { label: 'ISR a Pagar',     value: `RD$ ${fmt(liq.isr_a_pagar)}`, color: '#fd7e14' },
        ].map(k => (
          <div key={k.label} style={{ background: '#fff', border: `1px solid ${k.color}33`, borderLeft: `4px solid ${k.color}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#888' }}>{k.label}</div>
            <div style={{ fontWeight: 700, fontSize: 17, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={exportar} style={{ padding: '8px 20px', background: '#198754', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          ⬇ Exportar TXT
        </button>
      </div>

      {/* Estado de Resultados */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Estado de Resultados — Año {anio}</h3>
        <SeccionCuentas titulo="I. Ingresos" filas={er.ingresos} total={er.total_ingresos} color="#28a745" />
        <SeccionCuentas titulo="II. Costos" filas={er.costos} total={er.total_costos} color="#fd7e14" />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, background: '#f8f9fa', padding: '10px 12px', borderRadius: 8, marginBottom: 16, fontSize: 15 }}>
          <span>Utilidad Bruta</span>
          <span style={{ color: er.utilidad_bruta >= 0 ? '#28a745' : '#dc3545' }}>RD$ {fmt(er.utilidad_bruta)}</span>
        </div>
        <SeccionCuentas titulo="III. Gastos Deducibles" filas={er.gastos} total={er.total_gastos} color="#dc3545" />
      </div>

      {/* Liquidación */}
      <div style={{ ...cardStyle, border: `2px solid ${colorResultado}` }}>
        <h3 style={{ marginTop: 0, color: colorResultado }}>IV. Liquidación ISR</h3>
        {[
          { label: 'Renta Neta Imponible',            value: fmt(liq.renta_neta_imponible),    color: colorResultado },
          { label: `ISR ${liq.tasa_isr}`,             value: fmt(liq.isr_causado),              color: '#6f42c1' },
          { label: 'ISR retenido a empleados (ref.)', value: fmt(liq.isr_empleados_retenido),   color: '#888' },
          { label: 'TSS Patronal pagado (ref.)',       value: fmt(liq.tss_patronal_anual),       color: '#888' },
        ].map(r => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
            <span style={{ color: '#555' }}>{r.label}</span>
            <span style={{ fontWeight: 600, color: r.color }}>RD$ {r.value}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: 8, fontSize: 18, fontWeight: 800, borderTop: `2px solid ${colorResultado}` }}>
          <span>ISR A PAGAR</span>
          <span style={{ color: colorResultado }}>RD$ {fmt(liq.isr_a_pagar)}</span>
        </div>
        <p style={{ color: '#888', fontSize: 12, margin: '8px 0 0' }}>
          Tasa ISR empresas RD: 27% sobre renta neta positiva. Fecha límite de declaración: 120 días después del cierre fiscal.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Tab 609 – Pagos al Exterior
// ─────────────────────────────────────────────
const TIPOS_RENTA_609 = {
  '01': 'Rentas del trabajo dependiente',
  '02': 'Honorarios / trabajo independiente',
  '03': 'Rentas empresariales / servicios',
  '04': 'Rentas del capital mobiliario',
  '05': 'Rentas del capital inmobiliario',
  '06': 'Regalías',
  '07': 'Intereses',
  '08': 'Dividendos',
};

const PAISES_COMUNES = [
  'Estados Unidos', 'Alemania', 'Francia', 'España', 'Países Bajos',
  'Irlanda', 'Canadá', 'México', 'Colombia', 'Brasil', 'Chile',
  'Argentina', 'Panamá', 'Costa Rica', 'Puerto Rico', 'Reino Unido',
  'Suecia', 'Suiza', 'Singapur', 'Japón', 'Australia', 'India',
];

const formVacio = { beneficiario: '', pais: '', concepto: '', fecha: new Date().toISOString().split('T')[0], monto: '', tasa_retencion: '27', tipo_renta: '03', cuenta_gasto_codigo: '6.2.3', referencia: '' };

function Tab609({ datos, loading, mes, anio, exportarTxt, exportarExcel, recargar }) {
  const [form,          setForm]          = useState(formVacio);
  const [guardando,     setGuardando]     = useState(false);
  const [mostrarForm,   setMostrarForm]   = useState(false);
  const [cuentasGasto,  setCuentasGasto]  = useState([]);

  // Carga cuentas tipo egreso (clase 6) al montar el tab.
  // El endpoint devuelve un ÁRBOL: hay que aplanarlo recursivamente antes de
  // filtrar. Sin esto sólo se evaluaban las raíces —ninguna de las cuales
  // acepta movimientos— y el desplegable quedaba siempre vacío.
  useEffect(() => {
    const aplanar = (arr) => (arr || []).flatMap(c => [c, ...aplanar(c.hijos)]);
    api.get('/contabilidad/cuentas')
      .then(({ data }) => {
        const gastos = aplanar(data.cuentas || data || []).filter(c =>
          c.tipo === 'egreso' && c.acepta_movimientos && c.activa !== false
        );
        setCuentasGasto(gastos);
      })
      .catch(() => {});
  }, []);

  const MESES_ES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const montoRetenido = ((parseFloat(form.monto) || 0) * ((parseFloat(form.tasa_retencion) || 0) / 100)).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.beneficiario || !form.pais || !form.concepto || !form.fecha || !form.monto || !form.cuenta_gasto_codigo) {
      Swal.fire('Campos requeridos', 'Completa todos los campos obligatorios.', 'warning');
      return;
    }
    setGuardando(true);
    try {
      await api.post('/dgii/609', form);
      setForm(formVacio);
      setMostrarForm(false);
      recargar();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.error || 'No se pudo guardar el registro', 'error');
    } finally { setGuardando(false); }
  };

  const handleEliminar = async (id) => {
    const { isConfirmed } = await Swal.fire({
      title: '¿Eliminar registro?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/dgii/609/${id}`);
      recargar();
    } catch {
      Swal.fire('Error', 'No se pudo eliminar el registro', 'error');
    }
  };

  if (loading) return <Spinner />;

  const registros = datos?.registros || [];
  const totales   = datos?.totales   || { monto: 0, monto_retenido: 0 };

  return (
    <div>
      {/* Header */}
      <div style={s.reporteHeader}>
        <div>
          <h3 style={s.cardTitulo}>🌐 Formato 609 – Pagos al Exterior</h3>
          <p style={s.subtituloSeccion}>
            {registros.length} registros · Total RD$ {fmtMonto(totales.monto)} · Retención RD$ {fmtMonto(totales.monto_retenido)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={() => setMostrarForm(v => !v)} style={{ ...s.btnPrimario, background: mostrarForm ? '#6c757d' : '#0066cc' }}>
            {mostrarForm ? '✕ Cancelar' : '+ Nuevo Pago'}
          </button>
          <button onClick={exportarTxt} style={s.btnExportar}>⬇ TXT (DGII)</button>
          <button onClick={exportarExcel} style={{ ...s.btnExportar, background: '#217346' }}>⬇ Excel</button>
        </div>
      </div>

      {/* Tip informativo */}
      <div style={{ ...s.card, background: '#eff6ff', border: '1px solid #bfdbfe', marginBottom: '1rem', padding: '0.85rem 1.25rem' }}>
        <p style={{ margin: 0, color: '#1e40af', fontSize: '0.875rem' }}>
          💡 <strong>¿Qué va en el 609?</strong> Pagos a proveedores del exterior sin establecimiento en RD: AWS, Google Cloud, Microsoft 365, Zoom, Cloudflare, dominios, hosting, plataformas SaaS, licencias de software, servicios de consultoría, etc.
          La tasa de retención estándar es <strong>27%</strong> (Art. 305 Cód. Tributario), aunque puede variar según convenio de doble tributación.
        </p>
      </div>

      {/* Formulario nuevo registro */}
      {mostrarForm && (
        <div style={{ ...s.card, border: '2px solid #0066cc', marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 1.25rem', color: '#0066cc' }}>➕ Registrar Pago al Exterior</h4>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={sF.label}>Beneficiario *</label>
                <input style={sF.input} placeholder="ej. Amazon Web Services, Inc." value={form.beneficiario}
                  onChange={e => setForm(p => ({ ...p, beneficiario: e.target.value }))} required />
              </div>
              <div>
                <label style={sF.label}>País *</label>
                <input style={sF.input} list="paises-list" placeholder="ej. Estados Unidos" value={form.pais}
                  onChange={e => setForm(p => ({ ...p, pais: e.target.value }))} required />
                <datalist id="paises-list">
                  {PAISES_COMUNES.map(p => <option key={p} value={p} />)}
                </datalist>
              </div>
              <div>
                <label style={sF.label}>Tipo de Renta</label>
                <select style={sF.input} value={form.tipo_renta} onChange={e => setForm(p => ({ ...p, tipo_renta: e.target.value }))}>
                  {Object.entries(TIPOS_RENTA_609).map(([k, v]) => (
                    <option key={k} value={k}>{k} – {v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={sF.label}>Fecha *</label>
                <input style={sF.input} type="date" value={form.fecha}
                  onChange={e => setForm(p => ({ ...p, fecha: e.target.value }))} required />
              </div>
              <div>
                <label style={sF.label}>Monto RD$ *</label>
                <input style={sF.input} type="number" step="0.01" min="0" placeholder="0.00" value={form.monto}
                  onChange={e => setForm(p => ({ ...p, monto: e.target.value }))} required />
              </div>
              <div>
                <label style={sF.label}>Tasa Retención % *</label>
                <input style={sF.input} type="number" step="0.01" min="0" max="100" placeholder="27" value={form.tasa_retencion}
                  onChange={e => setForm(p => ({ ...p, tasa_retencion: e.target.value }))} required />
              </div>
              <div>
                <label style={sF.label}>Cuenta de Gasto *</label>
                <select style={sF.input} value={form.cuenta_gasto_codigo}
                  onChange={e => setForm(p => ({ ...p, cuenta_gasto_codigo: e.target.value }))} required>
                  {cuentasGasto.length === 0 && (
                    <option value="6.2.3">6.2.3 — Comunicaciones (por defecto)</option>
                  )}
                  {cuentasGasto.map(c => (
                    <option key={c.id} value={c.codigo}>
                      {c.codigo} — {c.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={sF.label}>Referencia / Factura</label>
                <input style={sF.input} placeholder="Nro. factura o referencia" value={form.referencia}
                  onChange={e => setForm(p => ({ ...p, referencia: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.6rem 1rem', width: '100%' }}>
                  <div style={{ fontSize: '0.78rem', color: '#065f46', fontWeight: 600 }}>ISR a Retener</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#059669' }}>RD$ {fmtMonto(montoRetenido)}</div>
                </div>
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={sF.label}>Concepto *</label>
              <textarea style={{ ...sF.input, minHeight: 60, resize: 'vertical' }} placeholder="Descripción del servicio o pago..." value={form.concepto}
                onChange={e => setForm(p => ({ ...p, concepto: e.target.value }))} required />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setForm(formVacio); setMostrarForm(false); }}
                style={{ ...s.btnPrimario, background: '#6c757d' }}>Cancelar</button>
              <button type="submit" style={s.btnPrimario} disabled={guardando}>
                {guardando ? 'Guardando...' : '💾 Guardar Registro'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* KPIs */}
      <div style={s.kpiGrid3}>
        <KpiMini titulo="Total Pagado al Exterior"  valor={`RD$ ${fmtMonto(totales.monto)}`}          color="#0066cc" />
        <KpiMini titulo="ISR Retenido (Enviado)"    valor={`RD$ ${fmtMonto(totales.monto_retenido)}`} color="#dc2626" />
        <KpiMini titulo="Neto Remesado"              valor={`RD$ ${fmtMonto(totales.monto - totales.monto_retenido)}`} color="#059669" />
      </div>

      {/* Tabla de registros */}
      {registros.length === 0 ? (
        <VacioMsg mensaje={`No hay pagos al exterior registrados en ${MESES_ES[mes-1]} ${anio}`} icono="🌐" color="#1e40af" bg="#eff6ff" />
      ) : (
        <div style={s.tablaWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Fecha','Beneficiario','País','Tipo Renta','Cuenta Gasto','Concepto','Monto RD$','Tasa %','Retención RD$','Ref.',''].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {registros.map((r, i) => (
                <tr key={r.id} style={{ ...s.tr, background: i % 2 === 0 ? '#fff' : '#f8faff' }}>
                  <td style={s.td}>{fmtFecha(r.fecha)}</td>
                  <td style={{ ...s.td, fontWeight: 600 }}>{r.beneficiario}</td>
                  <td style={s.td}><span style={s.ncfChip}>{r.pais}</span></td>
                  <td style={s.td}><span style={{ ...s.tipoChip }}>{r.tipo_renta}</span></td>
                  <td style={s.td}><span style={{ ...s.tipoChip, background: '#f0fdf4', color: '#166534' }}>{r.cuenta_gasto_codigo || '6.2.3'}</span></td>
                  <td style={{ ...s.td, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.concepto}>{r.concepto}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtMonto(r.monto)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{r.tasa_retencion}%</td>
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600, color: '#dc2626' }}>{fmtMonto(r.monto_retenido)}</td>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '0.8rem', color: '#6c757d' }}>{r.referencia || '—'}</td>
                  <td style={s.td}>
                    <button onClick={() => handleEliminar(r.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '1rem' }}
                      title="Eliminar">🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f0f4ff', fontWeight: 700 }}>
                <td colSpan={5} style={{ ...s.td, textAlign: 'right' }}>TOTALES</td>
                <td style={{ ...s.td, textAlign: 'right', color: '#0066cc' }}>{fmtMonto(totales.monto)}</td>
                <td style={s.td}></td>
                <td style={{ ...s.td, textAlign: 'right', color: '#dc2626' }}>{fmtMonto(totales.monto_retenido)}</td>
                <td colSpan={2} style={s.td}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// Estilos locales del formulario 609
const sF = {
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' },
  input: { width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none' },
};

// ─────────────────────────────────────────────
//  Tab IR-3 — ISR Personas Físicas con Negocio
// ─────────────────────────────────────────────
function TabIR3({ datos, loading, anio, exportar, cargar }) {
  const fmt = (n) => fmtMonto(parseFloat(n) || 0);
  const cardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem' };

  if (loading) return <Spinner />;
  if (!datos) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <p style={{ color: '#64748b', marginBottom: '1rem' }}>Carga la declaración IR-3 para el año fiscal {anio}</p>
      <button onClick={cargar} style={{ padding: '10px 24px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
        Generar IR-3
      </button>
    </div>
  );

  const { estado_resultados: er, liquidacion: liq } = datos;
  const colorResultado = liq.resultado === 'UTILIDAD' ? '#28a745' : '#dc3545';

  const SeccionCuentas = ({ titulo, filas, total, color }) => (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 8px', color, borderBottom: `1px solid ${color}44`, paddingBottom: 4 }}>{titulo}</h4>
      {filas.length === 0
        ? <p style={{ color: '#aaa', fontSize: 13 }}>Sin movimientos</p>
        : filas.map((f, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14, borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ color: '#555' }}><code style={{ fontSize: 11, color: '#888' }}>{f.codigo}</code> {f.nombre}</span>
            <span style={{ fontWeight: 600 }}>RD$ {fmt(f.monto)}</span>
          </div>
        ))
      }
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 8, padding: '6px 0', borderTop: `2px solid ${color}` }}>
        <span>Total</span>
        <span style={{ color }}>RD$ {fmt(total)}</span>
      </div>
    </div>
  );

  return (
    <div>
      {/* Aviso IR-3 vs IR-2 */}
      <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '0.85rem 1.25rem', marginBottom: '1.5rem' }}>
        <p style={{ margin: 0, color: '#5b21b6', fontSize: '0.875rem' }}>
          👤 <strong>IR-3 — Negocio de Único Dueño (Persona Física).</strong> Aplica escala progresiva (0%–25%) en lugar del 27% flat de las empresas.
          Fecha límite de presentación: <strong>31 de marzo</strong> del año siguiente (o 120 días desde el cierre fiscal).
        </p>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Ingresos',           value: `RD$ ${fmt(er.total_ingresos)}`,      color: '#28a745' },
          { label: 'Costos + Gastos',    value: `RD$ ${fmt(er.total_costos + er.total_gastos)}`, color: '#dc3545' },
          { label: 'Renta Neta',         value: `RD$ ${fmt(liq.renta_neta_imponible)}`, color: colorResultado },
          { label: 'ISR Determinado',    value: `RD$ ${fmt(liq.isr_causado)}`,         color: '#7c3aed' },
          { label: 'ISR a Pagar',        value: `RD$ ${fmt(liq.isr_a_pagar)}`,         color: '#fd7e14' },
        ].map(k => (
          <div key={k.label} style={{ background: '#fff', border: `1px solid ${k.color}33`, borderLeft: `4px solid ${k.color}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, color: '#888' }}>{k.label}</div>
            <div style={{ fontWeight: 700, fontSize: 17, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={exportar} style={{ padding: '8px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
          ⬇ Exportar TXT
        </button>
      </div>

      {/* Estado de resultados */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Estado de Resultados — Año {anio}</h3>
        <SeccionCuentas titulo="I. Ingresos" filas={er.ingresos} total={er.total_ingresos} color="#28a745" />
        <SeccionCuentas titulo="II. Costos" filas={er.costos} total={er.total_costos} color="#fd7e14" />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, background: '#f8f9fa', padding: '10px 12px', borderRadius: 8, marginBottom: 16, fontSize: 15 }}>
          <span>Utilidad Bruta</span>
          <span style={{ color: er.utilidad_bruta >= 0 ? '#28a745' : '#dc3545' }}>RD$ {fmt(er.utilidad_bruta)}</span>
        </div>
        <SeccionCuentas titulo="III. Gastos Deducibles" filas={er.gastos} total={er.total_gastos} color="#dc3545" />
      </div>

      {/* Escala ISR */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: '#7c3aed' }}>Escala ISR — Personas Físicas {anio}</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr>
              {['Tramo','Desde RD$','Hasta RD$','Tasa','Descripción'].map(h => (
                <th key={h} style={{ ...s.th, textAlign: h === 'Tasa' ? 'center' : 'left' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(liq.tramos_referencia || []).map((t, i) => {
              const activo = liq.renta_neta_imponible > t.desde && (t.hasta === null || liq.renta_neta_imponible <= t.hasta);
              return (
                <tr key={i} style={{ background: activo ? '#f5f3ff' : i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                  <td style={s.td}>{i + 1}{activo && <span style={{ ...s.badge, background: '#ede9fe', color: '#6d28d9', marginLeft: 8 }}>← aplica</span>}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtMonto(t.desde)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{t.hasta ? fmtMonto(t.hasta) : '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center', fontWeight: 700, color: '#7c3aed' }}>{t.tasa}</td>
                  <td style={s.td}>{t.descripcion}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Liquidación */}
      <div style={{ ...cardStyle, border: `2px solid ${colorResultado}` }}>
        <h3 style={{ marginTop: 0, color: colorResultado }}>Liquidación ISR</h3>
        {[
          { label: 'Renta Neta Imponible',               value: fmt(liq.renta_neta_imponible),   color: colorResultado },
          { label: 'ISR según escala personas físicas:',  value: fmt(liq.isr_causado),             color: '#7c3aed' },
          { label: '(-) ISR retenido en nómina (ref.):',  value: fmt(liq.isr_anticipos_nomina),    color: '#888' },
          { label: '(-) Retenciones pagos exterior:',     value: fmt(liq.retenciones_exterior),    color: '#888' },
        ].map(r => (
          <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
            <span style={{ color: '#555' }}>{r.label}</span>
            <span style={{ fontWeight: 600, color: r.color }}>RD$ {r.value}</span>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: 8, fontSize: 18, fontWeight: 800, borderTop: `2px solid ${colorResultado}` }}>
          <span>ISR A PAGAR</span>
          <span style={{ color: colorResultado }}>RD$ {fmt(liq.isr_a_pagar)}</span>
        </div>
        <p style={{ color: '#888', fontSize: 12, margin: '8px 0 0' }}>
          Escala progresiva Ley 11-92: 0% hasta RD$416,220 · 15% hasta RD$624,329 · 20% hasta RD$867,123 · 25% excedente.
          Presentación: 31 de marzo del año siguiente o 120 días del cierre fiscal.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  Estilos
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
//  Modal de errores de validación fiscal (606 y 607)
// ─────────────────────────────────────────────
function ModalErroresFiscales({ data, onCerrar, onIrACorregir }) {
  const { tipo = '606', detallados = [], generales = [] } = data || {};
  const reporte = REPORTES_CON_VALIDACION[tipo] || REPORTES_CON_VALIDACION['606'];

  // Agrupa por comprobante para mostrar una fila con todos sus problemas.
  // `contraparte` es el proveedor en el 606 y el cliente en el 607.
  const filas = [];
  const indice = new Map();
  for (const e of detallados) {
    const contraparte = e.contraparte ?? e.proveedor ?? null;
    const clave = `${e.factura || ''}|${contraparte || ''}|${e.ncf || ''}`;
    if (!indice.has(clave)) {
      indice.set(clave, filas.length);
      filas.push({ factura: e.factura, contraparte, ncf: e.ncf, problemas: [] });
    }
    filas[indice.get(clave)].problemas.push({ texto: e.problema, campo: e.campo });
  }

  const capitalizar = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);
  const CAMPOS_CLASIFICACION = ['tipo_bien_servicio_606', 'tipo_ingreso'];
  const chipColor = (campo) => CAMPOS_CLASIFICACION.includes(campo)
    ? { background: '#fef3c7', color: '#92400e' }   // ámbar (falta clasificar)
    : { background: '#fee2e2', color: '#991b1b' };  // rojo (otros datos faltantes)

  return (
    <div style={s.overlay} onClick={onCerrar}>
      <div style={s.modalBox} onClick={ev => ev.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#991b1b' }}>⚠️ No se puede generar el {reporte.titulo}</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: '#6c757d', fontSize: '0.88rem' }}>
              {filas.length > 0
                ? `${filas.length} ${filas.length === 1 ? 'factura requiere' : 'facturas requieren'} corrección antes de exportar`
                : 'Corrige los siguientes puntos antes de exportar'}
            </p>
          </div>
          <button onClick={onCerrar} style={s.btnCerrarX}>✕</button>
        </div>

        <div style={s.modalBody}>
          {filas.length > 0 && (
            <div style={{ ...s.tablaWrap, marginBottom: generales.length ? '1rem' : 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Factura', reporte.contraparte, 'NCF', 'Problema'].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                      <td style={{ ...s.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{f.factura || '—'}</td>
                      <td style={s.td}>{f.contraparte || '—'}</td>
                      <td style={s.td}>{f.ncf ? <span style={s.ncfChip}>{f.ncf}</span> : '—'}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                          {f.problemas.map((p, j) => (
                            <span key={j} style={{ ...s.badge, ...chipColor(p.campo), textTransform: 'none', letterSpacing: 0 }}>
                              {capitalizar(p.texto)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {generales.length > 0 && (
            <div style={s.avisoBox}>
              {generales.map((g, i) => (
                <div key={i} style={{ fontSize: '0.85rem', color: '#1e40af' }}>ℹ️ {g}</div>
              ))}
            </div>
          )}

          <p style={{ margin: '1rem 0 0 0', fontSize: '0.85rem', color: '#6c757d' }}>
            {tipo === '607'
              ? <>💡 El <strong>NCF</strong> y demás datos del comprobante se corrigen en <strong>Facturas</strong>, editando cada comprobante.</>
              : <>💡 El <strong>Tipo Bien/Servicio 606</strong> y demás datos del comprobante se corrigen en <strong>Cuentas por Pagar</strong>, editando cada factura.</>}
          </p>
        </div>

        <div style={s.modalFooter}>
          <button onClick={onCerrar} style={s.btnSecundario}>Cerrar</button>
          <button onClick={onIrACorregir} style={s.btnPrimario}>Ir a corregir →</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {
    padding: '2rem',
    maxWidth: 1300,
    margin: '0 auto',
    fontFamily: "'Segoe UI', sans-serif"
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '1rem',
    marginBottom: '1.5rem'
  },
  titulo: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0.5rem 0 0.25rem 0'
  },
  subtitulo: {
    color: '#6c757d',
    fontSize: '0.95rem',
    margin: 0
  },
  btnVolver: {
    background: 'none',
    border: 'none',
    color: '#0066cc',
    cursor: 'pointer',
    fontSize: '0.9rem',
    padding: 0,
    fontWeight: 600
  },
  filtrosRow: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  select: {
    padding: '0.55rem 0.9rem',
    border: '1px solid #dee2e6',
    borderRadius: 6,
    fontSize: '0.9rem',
    background: '#fff',
    cursor: 'pointer'
  },
  btnPrimario: {
    background: '#0066cc',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.6rem 1.4rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem',
    transition: 'all 0.2s'
  },
  btnExportar: {
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.6rem 1.4rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem'
  },
  tabsBar: {
    display: 'flex',
    gap: '0.5rem',
    borderBottom: '2px solid #e2e8f0',
    marginBottom: '1.5rem',
    overflowX: 'auto'
  },
  tabBtn: {
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    padding: '0.7rem 1.25rem',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#6c757d',
    whiteSpace: 'nowrap',
    marginBottom: -2,
    transition: 'all 0.15s'
  },
  tabActivo: {
    color: '#0066cc',
    borderBottomColor: '#0066cc'
  },
  contenido: {
    minHeight: 300
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem'
  },
  kpiGrid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1rem',
    marginBottom: '1.25rem'
  },
  card: {
    background: '#fff',
    borderRadius: 10,
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: '1.5rem'
  },
  cardTitulo: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 0.5rem 0'
  },
  subtituloSeccion: {
    color: '#6c757d',
    fontSize: '0.875rem',
    margin: 0
  },
  reporteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
    gap: '0.75rem'
  },
  tablaWrap: {
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflowX: 'auto'
  },
  tablaSimple: {
    overflowX: 'auto'
  },
  th: {
    background: '#f8f9fa',
    padding: '0.75rem 1rem',
    textAlign: 'left',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#495057',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    borderBottom: '2px solid #dee2e6',
    whiteSpace: 'nowrap'
  },
  td: {
    padding: '0.7rem 1rem',
    color: '#495057',
    borderBottom: '1px solid #f1f3f5',
    fontSize: '0.875rem'
  },
  tr: {
    transition: 'background 0.15s'
  },
  badge: {
    display: 'inline-block',
    padding: '0.25rem 0.6rem',
    borderRadius: 50,
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  ncfChip: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    background: '#e0f2fe',
    color: '#075985',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 600
  },
  tipoChip: {
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    background: '#f3f4f6',
    color: '#374151',
    borderRadius: 4,
    fontSize: '0.72rem',
    fontWeight: 700
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem'
  },
  modalBox: {
    background: '#fff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 780,
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #e2e8f0'
  },
  modalBody: {
    padding: '1.5rem'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e2e8f0'
  },
  btnCerrarX: {
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#6c757d',
    lineHeight: 1
  },
  btnSecundario: {
    background: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '0.6rem 1.2rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: '0.9rem'
  },
  avisoBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
    padding: '0.75rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem'
  }
};
